// hooks/auth/useRecoveryEmailOtp.ts
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthStatus,
  selectAuthError,
  selectForgotPasswordEmailAddress,
} from '@/store/selectors/auth';
import { verifyRecoveryOtpThunk } from '@/store/thunks/auth/verifyRecoveryOtpThunk';
import { forgotPasswordThunk } from '@/store/thunks/auth/forgotPasswordThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';
import { MIN_LEN, OTP_LENGTH } from '@/constants';

interface UseRecoveryEmailOtpState {
  otp: string;
  isLoading: boolean;
  globalError: string | null;
}

interface UseRecoveryEmailOtpHandlers {
  handleOtpChange: (newOtp: string) => void;
  handleSubmit: (event?: FormEvent) => void;
  handleResend: () => void;
  handleBack: () => void;
}

interface UseRecoveryEmailOtpReturn {
  state: UseRecoveryEmailOtpState;
  handlers: UseRecoveryEmailOtpHandlers;
}

/**
 * Hook for /reset-password/otp-verify route
 * Handles email OTP verification during password recovery
 */
export function useRecoveryEmailOtp(): UseRecoveryEmailOtpReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const emailAddress = useAppSelector(selectForgotPasswordEmailAddress);
  const isLoading = authStatus === 'loading';

  const [otp, setOtp] = useState('');

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const verify = useCallback(
    async(code: string) => {
      if (code.trim().length !== OTP_LENGTH) return;
      if (typeof emailAddress !== 'string' || emailAddress.trim().length === MIN_LEN) return;

      const action = await dispatch(verifyRecoveryOtpThunk({ emailAddress, code }));
      if (verifyRecoveryOtpThunk.fulfilled.match(action)) {
        await navigate({ to: '/reset-password/two-factor-verify' });
      }
    },
    [dispatch, navigate, emailAddress],
  );

  const handleOtpChange = useCallback(
    (newOtp: string): void => {
      setOtp(newOtp);
      if (newOtp.length === OTP_LENGTH) {
        void verify(newOtp);
      }
    },
    [verify],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent): void => {
      if (event !== undefined) event.preventDefault();
      if (otp.length === OTP_LENGTH) {
        void verify(otp);
      }
    },
    [otp, verify],
  );

  const handleResend = useCallback((): void => {
    if (typeof emailAddress !== 'string' || emailAddress.trim().length === MIN_LEN) return;
    setOtp('');
    void dispatch(forgotPasswordThunk({ emailAddress }));
  }, [dispatch, emailAddress]);

  const handleBack = useCallback((): void => {
    void navigate({ to: '/reset-password' });
  }, [navigate]);

  const globalError =
    typeof error === 'string' && error.length > MIN_LEN ? error : null;

  return {
    state: { otp, isLoading, globalError },
    handlers: { handleOtpChange, handleSubmit, handleResend, handleBack },
  };
}
